import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarRange, LayoutDashboard, ListOrdered, LogOut, Menu, Layers, Users, Wallet, X } from 'lucide-react';
import { ROLE_LABEL, useAccount, useIsAdmin } from '@/auth/account';
import { logout } from '@/auth/AuthGate';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui/Button';
import { cn } from '@/lib/cn';

const NAV = [
  { to: '/', label: 'Übersicht', icon: LayoutDashboard, end: true },
  { to: '/projekte', label: 'Projekte', icon: Layers, end: false },
  { to: '/prioritaeten', label: 'Prioritäten', icon: ListOrdered, end: false },
  { to: '/jahresplan', label: 'Jahresplan', icon: CalendarRange, end: false },
  { to: '/budget', label: 'Budget', icon: Wallet, end: false },
];

const ADMIN_NAV = [{ to: '/benutzer', label: 'Benutzer', icon: Users, end: false }];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const isAdmin = useIsAdmin();
  return (
    <nav className="flex flex-col gap-1">
      {[...NAV, ...(isAdmin ? ADMIN_NAV : [])].map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                : 'text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
            )
          }
        >
          <Icon size={17} aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden="true"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--accent)] text-[var(--accent-ink)] shadow-sm"
      >
        <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none" stroke="currentColor">
          <path
            d="M6 15 16 7l10 8v10a1.5 1.5 0 0 1-1.5 1.5h-5V19h-7v7.5h-5A1.5 1.5 0 0 1 6 25z"
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="leading-tight">
        <p className="text-sm font-semibold tracking-tight">Roths Haus Planer</p>
        <p className="text-[11px] text-[var(--ink-3)]">Investitionen & Budget</p>
      </div>
    </div>
  );
}

/** Name, role and logout – only shown when someone is signed in via SSO. */
function AccountBox() {
  const account = useAccount();
  if (!account || account.id === 'local') return null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border)] px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-[var(--ink)]">{account.name}</p>
        <p className="text-[11px] text-[var(--ink-3)]">{ROLE_LABEL[account.role]}</p>
      </div>
      <Button variant="ghost" size="icon" aria-label="Abmelden" title="Abmelden" onClick={() => void logout()}>
        <LogOut size={16} />
      </Button>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col gap-6 border-r border-[var(--border)] bg-[var(--surface)]/70 px-4 py-5 backdrop-blur-xl lg:flex">
        <Brand />
        <NavItems />
        <div className="mt-auto flex flex-col gap-3">
          <AccountBox />
          <ThemeToggle />
          <a
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-[var(--ink-3)] underline-offset-2 hover:text-[var(--ink-2)] hover:underline"
          >
            API-Dokumentation
          </a>
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--surface)]/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Brand />
        <Button
          variant="ghost"
          size="icon"
          aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </Button>
      </header>

      {menuOpen ? (
        <div className="border-b border-[var(--border)] bg-[var(--surface)] px-4 py-3 lg:hidden">
          <NavItems onNavigate={() => setMenuOpen(false)} />
          <div className="mt-3 flex flex-col gap-3">
            <AccountBox />
            <ThemeToggle />
          </div>
        </div>
      ) : null}

      <main className="min-w-0 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
    </div>
  );
}
